import type { WeldTelemetrySampleRow } from './weldTelemetryClient'
import { parseWeldTxtFilename, type WeldDirection } from './weldTxtFilenameParser'

export interface GraphAxisPoint {
  /** Sequential index for chart X positioning (0, 1, 2, …) */
  xIndex: number
  /** Display tilt label (raw stored value, or index+1 fallback) */
  tiltLabel: number
  /** Original sample index in input array */
  sampleIndex: number
}

export type DirectionLabel = WeldDirection
export type SegmentLegendLabel = string

export interface DirectionPassSegment {
  samples: WeldTelemetrySampleRow[]
  legendLabel: SegmentLegendLabel
}

export interface SegmentIndexPlotPoint {
  /** Sample index within the pass (0, 1, 2, …) */
  xIndex: number
  /** Raw tilt from the table row (for tooltips and tick labels) */
  rawTilt: number
}

/** Graph X-axis title — tilt is angular degrees, invariant across unit settings. */
export const GRAPH_TILT_AXIS_TITLE = 'Tilt (°)'

/** Default weld-position axis span (degrees). */
export const GRAPH_TILT_DEFAULT_MIN = 0
export const GRAPH_TILT_DEFAULT_MAX = 180

/** Padding beyond data extremes when they exceed the default 0–180° range. */
export const GRAPH_TILT_AXIS_PADDING = 5

/** Fixed overlay colors — stable across all parameter graphs. */
export const GRAPH_CW_LINE_COLOR = '#EB1C37'
export const GRAPH_CCW_LINE_COLOR = '#2563EB'

export interface SegmentLineStyle {
  borderColor: string
}

export interface SegmentDegreePlotPoint {
  /** Unwrapped tilt degrees for chart X (e.g. 352 → −8). */
  xDegree: number
  /** Raw stored tilt (tooltips). */
  rawTilt: number
}

function resolveTiltLabel(sample: WeldTelemetrySampleRow, index: number): number {
  if (typeof sample.tilt === 'number' && Number.isFinite(sample.tilt)) {
    return sample.tilt
  }
  return index + 1
}

function resolveEvtNo(sample: WeldTelemetrySampleRow): number {
  if (typeof sample.evtNo === 'number' && Number.isFinite(sample.evtNo)) {
    return sample.evtNo
  }
  return 0
}

/**
 * Build graph X-axis points in welding capture order. Unlike numeric tilt sort,
 * this preserves the 360° wrap-around sequence (e.g. 353 → 359 → 4 → … → 134).
 */
export function buildGraphAxisPoints(samples: WeldTelemetrySampleRow[]): GraphAxisPoint[] {
  return samples.map((sample, index) => ({
    xIndex: index,
    tiltLabel: resolveTiltLabel(sample, index),
    sampleIndex: index
  }))
}

/** Format a tilt value for axis ticks and tooltips. */
export function formatTiltLabel(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return String(Number(value.toFixed(2)))
}

/** Format a Y-axis tooltip value — at most 2 decimal places, no float noise. */
export function formatGraphTooltipValue(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return String(Number(value.toFixed(2)))
}

/** Metadata attached to each chart datum for hover tooltips. */
export interface GraphTooltipPointMeta {
  rawTilt: number
  /** 1-based Sl.no from the full table (unsorted source order). */
  slNo?: number
  /** Event number from the telemetry row. */
  evtNo?: number
  /** Internal id — not shown in the tooltip title. */
  sampleId?: number
}

/**
 * Tooltip title for a hovered point. Always uses the table/raw tilt — never the
 * unwrapped chart X — so wrap-start points (e.g. 182° plotted at −178) stay clear.
 * Prefers Sl.no + Event so users can match the Graph hover to the Table row.
 */
export function formatPassAnalyticsTooltipTitle(
  point: GraphTooltipPointMeta | null | undefined
): string {
  if (!point || typeof point.rawTilt !== 'number' || !Number.isFinite(point.rawTilt)) {
    return ''
  }
  const parts: string[] = []
  if (typeof point.slNo === 'number' && Number.isFinite(point.slNo) && point.slNo > 0) {
    parts.push(`Sl.no ${point.slNo}`)
  }
  if (typeof point.evtNo === 'number' && Number.isFinite(point.evtNo) && point.evtNo > 0) {
    parts.push(`Event ${point.evtNo}`)
  }
  parts.push(`Tilt: ${formatTiltLabel(point.rawTilt)}°`)
  return parts.join(' · ')
}

/**
 * Tooltip body line for one hovered series point. Always includes the Y-axis
 * parameter (Travel Speed, Current, Volts, …) plus unit. When CW/CCW overlay,
 * prefixes the direction so series stay distinct.
 */
export function formatPassAnalyticsTooltipLabel(input: {
  /** CW / CCW / Pass label — empty for single-series charts. */
  seriesLabel?: string
  /** Y-axis parameter name from the chart (e.g. Current). */
  parameterLabel: string
  value: number
  unitSuffix?: string
}): string {
  const formatted = formatGraphTooltipValue(input.value)
  const withUnit = input.unitSuffix ? `${formatted} ${input.unitSuffix}` : formatted
  const param = input.parameterLabel.trim()
  const valuePart = param ? `${param}: ${withUnit}` : withUnit
  const series = (input.seriesLabel ?? '').trim()
  if (!series || series === param) return valuePart
  return `${series} · ${valuePart}`
}

/** Legend label from a segment's evt_no range (e.g. "Event 132 - Event 164"). */
export function formatSegmentEventRangeLabel(segment: DirectionPassSegment): string {
  if (segment.samples.length === 0) return 'Event —'
  const evts = segment.samples
    .map((sample) => resolveEvtNo(sample))
    .filter((evt) => evt > 0)
  if (evts.length === 0) return 'Event —'
  const min = Math.min(...evts)
  const max = Math.max(...evts)
  if (min === max) return `Event ${min}`
  return `Event ${min} - Event ${max}`
}

/** Step size for evenly spaced tilt tick labels on large passes (legacy index axis). */
export function graphTiltTickStep(sampleCount: number, maxTicks = 15): number {
  if (sampleCount <= maxTicks) return 1
  return Math.ceil(sampleCount / maxTicks)
}

/** True when capture order jumps to a new direction pass (tilt reset near 360°). */
export function isDirectionPassBoundary(prevTilt: number, currTilt: number): boolean {
  if (prevTilt <= 180 && currTilt >= 300) return true
  if (currTilt - prevTilt > 90 && currTilt >= 300) return true
  return false
}

/** True when consecutive event numbers skip (e.g. evt 164 → 364 between weld laps). */
export function isEventPassBoundary(prevEvt: number, currEvt: number): boolean {
  return currEvt - prevEvt > 1
}

/** True when consecutive raw tilts cross the 360° wrap (e.g. 359 → 4). */
export function isTiltWrapBoundary(prevTilt: number, currTilt: number): boolean {
  return prevTilt > 180 && currTilt < 180 && prevTilt - currTilt > 90
}

/**
 * True when the same direction starts a new half-lap without an evt gap — e.g.
 * tilt falls from ~166 back to ~59 after completing the first strip. Excludes
 * 360° wrap points (355 → 29) which stay within one lap.
 */
export function isLapRestartBoundary(prevTilt: number, currTilt: number): boolean {
  if (isTiltWrapBoundary(prevTilt, currTilt)) return false
  return prevTilt >= 100 && currTilt <= 100 && prevTilt - currTilt > 40
}

/**
 * Unwrap raw tilt readings into a continuous degree progression for graphing.
 * When a lap starts past 180° (e.g. 352, 357), values are shifted so the wrap
 * maps through negatives: 352 → −8, 357 → −3, then 4 … 164.
 */
export function unwrapTilts(tilts: number[]): number[] {
  if (tilts.length === 0) return []
  const continuous = [tilts[0]]
  for (let i = 1; i < tilts.length; i++) {
    let curr = tilts[i]
    const prev = continuous[i - 1]
    while (curr - prev > 180) curr -= 360
    while (curr - prev < -180) curr += 360
    continuous.push(curr)
  }
  if (continuous[0] > GRAPH_TILT_DEFAULT_MAX) {
    return continuous.map((t) => t - 360)
  }
  return continuous
}

/**
 * X-axis bounds from unwrapped tilts: default 0–180; expand 5° past extremes
 * only when data falls outside that range. Also returns unpadded data extremes
 * for tick labeling (first label = dataMin).
 */
export function computeTiltAxisBounds(unwrappedTilts: number[]): {
  min: number
  max: number
  dataMin: number
  dataMax: number
} {
  if (unwrappedTilts.length === 0) {
    return {
      min: GRAPH_TILT_DEFAULT_MIN,
      max: GRAPH_TILT_DEFAULT_MAX,
      dataMin: GRAPH_TILT_DEFAULT_MIN,
      dataMax: GRAPH_TILT_DEFAULT_MAX
    }
  }
  const dataMin = Math.min(...unwrappedTilts)
  const dataMax = Math.max(...unwrappedTilts)
  return {
    min: dataMin < GRAPH_TILT_DEFAULT_MIN ? dataMin - GRAPH_TILT_AXIS_PADDING : GRAPH_TILT_DEFAULT_MIN,
    max: dataMax > GRAPH_TILT_DEFAULT_MAX ? dataMax + GRAPH_TILT_AXIS_PADDING : GRAPH_TILT_DEFAULT_MAX,
    dataMin,
    dataMax
  }
}

/** Collect unwrapped tilts from all overlay segments and compute shared axis bounds. */
export function computeGraphTiltAxisBounds(segments: DirectionPassSegment[]): {
  min: number
  max: number
  dataMin: number
  dataMax: number
} {
  const all: number[] = []
  for (const segment of segments) {
    const raw = extractSegmentTilts(segment)
    all.push(...unwrapTilts(raw))
  }
  return computeTiltAxisBounds(all)
}

/** Tick step for a numeric degree axis (legacy / non-extended spans). */
export function graphTiltDegreeTickStep(min: number, max: number): number {
  const span = max - min
  if (span <= 30) return 5
  if (span <= 90) return 10
  return 15
}

/**
 * Whether data extends past the default 0–180° weld-position window
 * (wrap-start negatives or values above 180).
 */
export function isExtendedTiltAxis(dataMin: number, dataMax: number): boolean {
  return dataMin < GRAPH_TILT_DEFAULT_MIN || dataMax > GRAPH_TILT_DEFAULT_MAX
}

const GRAPH_TILT_TICK_STEP = 15
/** Skip extra overflow ticks closer than this to another label (−9 then −5 would overlap). */
const GRAPH_TILT_MIN_LABEL_GAP = 10
/** Do not label wrap starts of −4, −3, −2, −1 — only −5° and further left. */
const GRAPH_TILT_FIRST_LABEL_MAX = -5

function nearlyEqualDegree(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9
}

function tooCloseForTiltLabel(a: number, b: number): boolean {
  return Math.abs(a - b) < GRAPH_TILT_MIN_LABEL_GAP - 1e-9
}

function pushUniqueDegreeTick(ticks: number[], value: number): void {
  const v = Number(value.toFixed(10))
  if (ticks.length === 0 || !nearlyEqualDegree(ticks[ticks.length - 1], v)) {
    ticks.push(v)
  }
}

/**
 * Build X tick values. Default 0–180 axes use 15° steps from 0.
 * Extended axes: first label is the wrap start when it is ≤ −5° (e.g. −9);
 * −4/−3/−2/−1 are not labeled. Then 0…180 at 15°. Last label is always 180.
 */
export function buildTiltDegreeTickValues(input: {
  dataMin: number
  dataMax: number
  axisMax: number
}): number[] {
  const { dataMin, dataMax, axisMax } = input
  if (!Number.isFinite(dataMin) || !Number.isFinite(axisMax)) return []

  if (!isExtendedTiltAxis(dataMin, dataMax)) {
    const step = graphTiltDegreeTickStep(GRAPH_TILT_DEFAULT_MIN, axisMax)
    const ticks: number[] = []
    for (let v = GRAPH_TILT_DEFAULT_MIN; v <= axisMax + 1e-9; v += step) {
      ticks.push(Number(v.toFixed(10)))
    }
    return ticks
  }

  const ticks: number[] = []

  if (dataMin < GRAPH_TILT_DEFAULT_MIN) {
    if (dataMin <= GRAPH_TILT_FIRST_LABEL_MAX) {
      pushUniqueDegreeTick(ticks, dataMin)
    }
    const overflowBelow: number[] = []
    for (let v = -GRAPH_TILT_TICK_STEP; v >= dataMin - 1e-9; v -= GRAPH_TILT_TICK_STEP) {
      overflowBelow.push(Number(v.toFixed(10)))
    }
    overflowBelow.reverse()
    for (const v of overflowBelow) {
      if (nearlyEqualDegree(v, dataMin)) continue
      if (tooCloseForTiltLabel(v, dataMin)) continue
      if (tooCloseForTiltLabel(v, GRAPH_TILT_DEFAULT_MIN)) continue
      pushUniqueDegreeTick(ticks, v)
    }
  }

  const mainMax = Math.min(GRAPH_TILT_DEFAULT_MAX, axisMax)
  for (let v = GRAPH_TILT_DEFAULT_MIN; v <= mainMax + 1e-9; v += GRAPH_TILT_TICK_STEP) {
    pushUniqueDegreeTick(ticks, v)
  }

  return ticks
}

/**
 * Whether to show a degree tick label. Hides padded axis bounds that are not on
 * the step grid (e.g. −25 when step is 15) so labels stay evenly spaced.
 */
export function shouldShowTiltDegreeTick(degree: number, stepSize: number): boolean {
  if (!Number.isFinite(degree) || !Number.isFinite(stepSize) || stepSize <= 0) return false
  const ratio = degree / stepSize
  return Math.abs(ratio - Math.round(ratio)) < 1e-9
}

/** Resolve CW/CCW for a segment — prefers stored bugType, then filename direction. */
export function resolveSegmentDirection(
  sample: WeldTelemetrySampleRow | undefined,
  sourceFilenames: string[] | undefined
): WeldDirection | null {
  if (sample?.bugType === 'CW' || sample?.bugType === 'CCW') {
    return sample.bugType
  }
  if (
    sourceFilenames &&
    sample &&
    typeof sample.sourceFileIndex === 'number' &&
    sample.sourceFileIndex >= 0 &&
    sample.sourceFileIndex < sourceFilenames.length
  ) {
    const parsed = parseWeldTxtFilename(sourceFilenames[sample.sourceFileIndex])
    if (parsed.direction === 'CW' || parsed.direction === 'CCW') {
      return parsed.direction
    }
  }
  return null
}

/**
 * Assign legend labels to split segments. One label per bug type only — always
 * bare "CW" / "CCW" (never "CW Pass 2" / "CCW Pass 2"). Unknown-direction
 * segments use neutral "Segment N" labels (never invent CW/CCW).
 */
export function buildSegmentLegendLabels(
  groups: WeldTelemetrySampleRow[][],
  sourceFilenames: string[] | undefined
): SegmentLegendLabel[] {
  const directions = groups.map((group) => resolveSegmentDirection(group[0], sourceFilenames))

  let unknownCount = 0
  for (const dir of directions) {
    if (dir !== 'CW' && dir !== 'CCW') unknownCount++
  }

  let unknownPassNum = 0

  return directions.map((dir) => {
    if (dir === 'CW' || dir === 'CCW') return dir
    if (unknownCount === 1) return 'Segment 1'
    unknownPassNum++
    return `Segment ${unknownPassNum}`
  })
}

/** Chart.js solid line color for one overlay segment (fixed; ignores param color). */
export function resolveSegmentLineStyle(legendLabel: string): SegmentLineStyle {
  if (legendLabel === 'CCW' || legendLabel.startsWith('CCW ')) {
    return { borderColor: GRAPH_CCW_LINE_COLOR }
  }
  if (legendLabel === 'CW' || legendLabel.startsWith('CW ')) {
    return { borderColor: GRAPH_CW_LINE_COLOR }
  }
  // Neutral Segment N — alternate CW/CCW palette without implying direction
  const segmentMatch = legendLabel.match(/^Segment (\d+)$/)
  const segmentNum = segmentMatch ? Number.parseInt(segmentMatch[1], 10) : 1
  if (segmentNum % 2 === 0) return { borderColor: GRAPH_CCW_LINE_COLOR }
  return { borderColor: GRAPH_CW_LINE_COLOR }
}

/**
 * Group samples into at most one bucket per bug type (CW / CCW / unknown).
 * Preserves first-seen direction order and capture order within each bucket.
 * Does not split on evt gaps, lap restarts, or source files — Dan: one series
 * per bug type only (no CW Pass 2 / CCW Pass 2).
 */
function splitIntoRawSegments(
  samples: WeldTelemetrySampleRow[],
  sourceFilenames?: string[]
): WeldTelemetrySampleRow[][] {
  if (samples.length === 0) return []

  const buckets = new Map<string, WeldTelemetrySampleRow[]>()
  const order: string[] = []

  for (const sample of samples) {
    const dir = resolveSegmentDirection(sample, sourceFilenames)
    const key = dir ?? '__unknown__'
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = []
      buckets.set(key, bucket)
      order.push(key)
    }
    bucket.push(sample)
  }

  return order.map((key) => buckets.get(key)!)
}

/** Default min |Δx°| on the unwrapped tilt axis that must break the chart line. */
export const GRAPH_TILT_LINE_BREAK_DELTA = 90

/**
 * Sentinel: never break the stroke (pre–gap-break continuous line).
 * Stored as project `graphTiltLineBreakDelta = -1`.
 */
export const TILT_GAP_BREAK_DISABLED = -1

/** Inclusive range for numeric tilt gap break thresholds (°). */
export const TILT_GAP_BREAK_MIN = 0
export const TILT_GAP_BREAK_MAX = 360

/** Dropdown values: No break (−1), then every integer 0° … 360°. */
export const TILT_GAP_BREAK_OPTIONS: readonly number[] = [
  TILT_GAP_BREAK_DISABLED,
  ...Array.from(
    { length: TILT_GAP_BREAK_MAX - TILT_GAP_BREAK_MIN + 1 },
    (_, i) => TILT_GAP_BREAK_MIN + i
  )
]

export function isTiltGapBreakDisabled(value: unknown): boolean {
  const n = typeof value === 'number' ? value : Number(value)
  return n === TILT_GAP_BREAK_DISABLED
}

export function isTiltGapBreakOption(value: unknown): value is number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isInteger(n)) return false
  if (n === TILT_GAP_BREAK_DISABLED) return true
  return n >= TILT_GAP_BREAK_MIN && n <= TILT_GAP_BREAK_MAX
}

export function normalizeTiltGapBreakDelta(value: unknown): number {
  return isTiltGapBreakOption(value) ? value : GRAPH_TILT_LINE_BREAK_DELTA
}

/** Label for the gap-break control / dropdown (e.g. "No break", "90°"). */
export function formatTiltGapBreakLabel(value: unknown): string {
  const n = normalizeTiltGapBreakDelta(value)
  if (isTiltGapBreakDisabled(n)) return 'No break'
  return `${n}°`
}

/**
 * True when consecutive unwrapped X degrees jump far enough to draw a false chord.
 * `delta` is the project setting (default 90°). `0` breaks on any non-zero jump.
 * `TILT_GAP_BREAK_DISABLED` (−1) never breaks (legacy continuous line).
 */
export function shouldBreakTiltLine(
  prevXDegree: number,
  currXDegree: number,
  delta: number = GRAPH_TILT_LINE_BREAK_DELTA
): boolean {
  if (!Number.isFinite(prevXDegree) || !Number.isFinite(currXDegree)) return false
  if (isTiltGapBreakDisabled(delta)) return false
  const threshold =
    Number.isFinite(delta) && delta >= TILT_GAP_BREAK_MIN && delta <= TILT_GAP_BREAK_MAX
      ? delta
      : GRAPH_TILT_LINE_BREAK_DELTA
  return Math.abs(currXDegree - prevXDegree) > threshold
}

/** Build index-based plot points for one pass segment (legacy / tests). */
export function buildSegmentIndexPlotPoints(segment: DirectionPassSegment): SegmentIndexPlotPoint[] {
  return segment.samples.map((sample, i) => ({
    xIndex: i,
    rawTilt: resolveTiltLabel(sample, i)
  }))
}

/** Build degree-axis plot points — x is unwrapped tilt for overlay on 0–180°. */
export function buildSegmentDegreePlotPoints(segment: DirectionPassSegment): SegmentDegreePlotPoint[] {
  const rawTilts = extractSegmentTilts(segment)
  const unwrapped = unwrapTilts(rawTilts)
  return segment.samples.map((sample, i) => ({
    xDegree: unwrapped[i] ?? resolveTiltLabel(sample, i),
    rawTilt: resolveTiltLabel(sample, i)
  }))
}

/** X-axis bounds for sample-index plotting (0 … count − 1). */
export function computeIndexAxisBounds(maxSampleCount: number): { min: number; max: number } {
  if (maxSampleCount <= 0) return { min: 0, max: 0 }
  return { min: 0, max: maxSampleCount - 1 }
}

/** Tilt labels from the primary (first) pass — used for index-axis tick labels. */
export function buildPrimarySegmentTiltLabels(segments: DirectionPassSegment[]): number[] {
  if (segments.length === 0) return []
  return extractSegmentTilts(segments[0])
}

/**
 * Tilt labels for overlay charts — one label per x index from the longest lap
 * segment (matches computeIndexAxisBounds / max segment sample count).
 */
export function buildOverlayAxisTiltLabels(segments: DirectionPassSegment[]): number[] {
  if (segments.length === 0) return []
  const longest = segments.reduce((longestSeg, seg) =>
    seg.samples.length >= longestSeg.samples.length ? seg : longestSeg
  )
  return extractSegmentTilts(longest)
}

/** Tilt labels in table row order — x-axis index 0 matches the first table row. */
export function buildTableOrderTiltLabels(samples: WeldTelemetrySampleRow[]): number[] {
  return samples.map((sample, index) => resolveTiltLabel(sample, index))
}

/**
 * Split merged capture-order samples into at most one CW and one CCW series
 * (plus a single unknown bucket when direction is missing). Same-direction
 * strips / evt gaps / files stay on one line; the graph breaks the stroke at
 * large tilt jumps instead of inventing Pass 2 overlays.
 */
export function splitSamplesByDirectionPass(
  samples: WeldTelemetrySampleRow[],
  sourceFilenames?: string[]
): DirectionPassSegment[] {
  if (samples.length === 0) return []

  const groups = splitIntoRawSegments(samples, sourceFilenames)
  const labels = buildSegmentLegendLabels(groups, sourceFilenames)

  return groups.map((group, index) => ({
    samples: group,
    legendLabel: labels[index]
  }))
}

/**
 * Drop unselected samples from already-split segments without re-running
 * evt/bugType/file boundaries. Used so checkbox unselect does not invent a
 * new pass (and a false −178° unwrap) from the hole left in evt_no sequence.
 * Legend labels stay those assigned on the full sample list.
 * Empty segments are removed.
 */
export function filterSegmentsToSelectedSamples(
  segments: DirectionPassSegment[],
  selectedSampleIds: ReadonlySet<number> | null | undefined
): DirectionPassSegment[] {
  if (!selectedSampleIds) return segments
  return segments
    .map((segment) => ({
      legendLabel: segment.legendLabel,
      samples: segment.samples.filter((sample) => selectedSampleIds.has(sample.sampleId))
    }))
    .filter((segment) => segment.samples.length > 0)
}

/**
 * Split on the full capture list, then keep only selected samples for plotting.
 * Preserves real pass boundaries while ignoring gaps created by unselect.
 */
export function buildPlotSegmentsForSelection(
  samples: WeldTelemetrySampleRow[],
  selectedSampleIds: ReadonlySet<number> | null | undefined,
  sourceFilenames?: string[]
): DirectionPassSegment[] {
  const split = splitSamplesByDirectionPass(samples, sourceFilenames)
  return filterSegmentsToSelectedSamples(split, selectedSampleIds)
}

export function extractSegmentTilts(segment: DirectionPassSegment): number[] {
  return segment.samples.map((s, i) => resolveTiltLabel(s, i))
}
